/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { NotificationModel } from '../../../../../core/models';
import { UtilsService } from '../../../../../core/services';
import { WidgetBaseComponent } from '../widgetBase.component';
import { WidgetInterface } from '../../interfaces/widget.interface';
import { WidgetStateEnum } from '../../../../../core/enums';

@Component({
    selector: 'app-lb-widget-extended-alerts',
    templateUrl: './extendedAlerts.widget.html',
    styleUrls: ['./extendedAlerts.widget.scss']
})

export class ExtendedAlertsWidgetComponent extends WidgetBaseComponent implements WidgetInterface, OnInit, AfterViewInit {
    /**
     * Content Object
     */
    @Input() public callBackFunction: any;

    /**
     * Incoming status object.
     * @param data
     */
    @Input() set externalStatuses(data: any) {
        this.formatResponse(data);
    }

    /**
     * Define number of columns each widget should span.
     */
    @Input()
    public colSpan: number;

    /**
     * Disable the widget data
     */
    @Input()
    public disabled = false;

    /**
     * Get frontend id from parent component.
     */
    @Input()
    public frontendId: string;

    /**
     * Open enum to view
     */
    public WidgetStateEnum = WidgetStateEnum;

    /**
     * Set our default state. Used to set the state of the widget, critical, warning or ok.
     */
    public state: WidgetStateEnum;

    /**
     * Store our notifications
     */
    public notifications: NotificationModel[] = [];

    /**
     * Widget loading state
     */
    public loading = false;

    /**
     * Total number of alerts for each type, critical and warning.
     */
    public totalAlerts = {
        critical: 0,
        warnings: 0,
        pending: 0
    };

    /**
     * Socket data object store
     */
    public socketData: any = {};

    /**
     * Positive message
     */
    public awesomeText = `
        Everything is awesome, everything is cool when all your servers are online.
        Everything is awesome, when your service is running like a dream.`;

    /**
     * Array of messages to be displayed if data is not being collected.
     */
    public statusMessage = 'widget_collecting_data';

    /**
     * Data structure for the view.
     */
    public widgetData: any [] = [];

    /**
     * Click counter incrementer.
     */
    public clickCounter = 0;

    constructor(
        utils: UtilsService
    ) {
        super(utils);
    }

    public ngOnInit(): void {
        this.loading = true;
    }

    public ngAfterViewInit(): void {
        // Just in case there is no socket data after the timer runs out display useful info.
        const noDataTimer = setTimeout(() => {
            this.loading = false;
            if (!this.widgetData.length) {
                this.statusMessage = 'widget_no_data_collected';
            }
            clearTimeout(noDataTimer);
        }, 3000);
    }

    // Widget defaults
    /**
     * Define all of our socket listeners
     */
    public socketListeners(): void {
    }

    /**
     * Get all of our widget details
     */
    public getWidgetDetails(): void {
    }

    /**
     * Reset all of our local variables
     */
    public resetLocalVariables(): void {
        this.notifications = [];
        this.state = WidgetStateEnum.OK;
    }

    // Widget specific functions
    /**
     * Get total number of warning and critical alerts.
     */
    public getTotalAlerts(): number {
        const total = 0;

        return total + (this.totalAlerts.critical + this.totalAlerts.warnings);
    }

    /**
     * Get widget status.
     * @param state
     */
    public getWidgetStatusClass(state: any): string {
        if (state === WidgetStateEnum.OK) {
            return 'default';
        } else if (state === WidgetStateEnum.WARNING) {
            return 'warning';
        } else if (state === WidgetStateEnum.CRITICAL) {
            return 'critical';
        }
    }

    /**
     * Get subheader message.
     */
    public getSubHeaderMessage(): string {
        if (this.totalAlerts.critical) {
            return 'Critical';
        } else if (this.totalAlerts.critical === 0 && this.totalAlerts.warnings) {
            return this.totalAlerts.warnings > 1 ? 'Warnings' : 'Warning';
        } else if (this.totalAlerts.pending) {
            return 'Pending';
        }
    }

    /**
     * Positive message.
     */
    public clickIncrementer(): number {
        if (this.statusMessage === 'widget_everything_good') {
            if (this.clickCounter === 7) {
                this.clickCounter = 0;
            }

            return this.clickCounter++;
        }
    }

    /**
     * Adds up all the warnings and returns the total
     * @param row
     */
    public totalWarningsByBackend(row: any): number {
        return row.ripsByState.warning.length + row.ripsByState.maint.length;
    }

    /**
     * Format the data for use in the widget.
     * @private
     */
    private formatResponse(data: any): void {
        // Only make changes if the data has changed
        if (JSON.stringify(data) !== JSON.stringify(this.socketData)) {
            if (data?.backends) {
                // Reset values
                this.socketData = data;
                this.statusMessage = '';
                this.totalAlerts.critical = 0;
                this.totalAlerts.warnings = 0;
                this.widgetData = [];
                this.loading = false;

                let ripsState: { up: any[]; maint: any[]; warning: any[]; down: any[]; };

                for (const backend in data.backends) {
                    if (data.backends.hasOwnProperty(backend)) {
                        const backendData: any = data.backends[backend];
                        ripsState = this.createStatusObject(backendData.reals);

                        if (backendData.status === 'failure') {
                            // If the backend is down send a false alert to the incrementer.
                            const falseAlert = {
                                up: [],
                                maint: [],
                                warning: [],
                                down: [1]
                            };
                            this.totalAlertIncrementer(falseAlert);
                        } else {
                            this.totalAlertIncrementer(ripsState);
                        }

                        this.widgetData.push({
                            name: backendData.name,
                            status: backendData.status,
                            ripsByState: ripsState
                        });
                    }
                }

                this.sortWidgetDataByAlert();
                // Once all data is sorted set widget state.
                this.setState();

                this.createAdditionalStatusMessage();
            }
        }
    }

    /**
     * Create an object with the real servers sorted into their status groups
     * @param serversObject
     * @private
     */
    private createStatusObject(serversObject: any[]): any {
        const serverArray: any[] = this.convertServerObjectToArray(serversObject);

        return {
            up: serverArray ? serverArray.filter(server => server.status.toLowerCase() === 'online') : [],
            maint: serverArray ? serverArray.filter(server => server.status.toLowerCase() === 'maintenance') : [],
            warning: serverArray ? serverArray.filter(server => server.status.toLowerCase() === 'warning') : [],
            down: serverArray ? serverArray.filter(server => server.status.toLowerCase() === 'failure') : []
        };
    }

    /**
     * Convert the server object into an array.
     * @param serverObject
     * @private
     */
    private convertServerObjectToArray(serverObject: any): any[] {
        const convertedObject: any[] = [];
        for (const obj in serverObject) {
            if (serverObject.hasOwnProperty(obj)) {
                convertedObject.push(serverObject[obj]);
            }
        }

        return convertedObject;
    }

    /**
     * Add up all alerts of a type, critical or warning
     * @param alert
     * @private
     */
    private totalAlertIncrementer(alert: any): void {
        if (alert) {
            this.totalAlerts.critical = this.totalAlerts.critical + alert.down.length;
            this.totalAlerts.warnings = this.totalAlerts.warnings + alert.maint.length + alert.warning.length;
        }
    }

    /**
     * Sort the widget data to critical first order, warnings second.
     * @private
     */
    private sortWidgetDataByAlert(): void {
        this.widgetData.sort((a, b) => {
            // First list backends that are down.
            if (b.status.toLowerCase() === 'down' && a.status.toLowerCase() !== 'down') {
                return 1;
            }

            if (a.status.toLowerCase() === 'down' && b.status.toLowerCase() !== 'down') {
                return -1;
            }

            // Then sort rips that are down
            if (a.ripsByState.down.length < b.ripsByState.down.length) {
                return 1;
            }

            if (a.ripsByState.down.length > b.ripsByState.down.length) {
                return -1;
            }

            // Then sort warnings
            if (this.totalWarningsByBackend(a) > this.totalWarningsByBackend(b)) {
                return -1;
            }

            if (this.totalWarningsByBackend(a) < this.totalWarningsByBackend(b)) {
                return 1;
            }

            return 0;
        });
    }

    /**
     * Set the widget state
     */
    private setState(): void {
        if (this.totalAlerts.critical) {
            this.state = WidgetStateEnum.CRITICAL;
        } else if (this.totalAlerts.warnings) {
            this.state = WidgetStateEnum.WARNING;
        } else {
            this.state = WidgetStateEnum.OK;
        }
    }

    /**
     * Additional status message if there is no data received by socket data.
     * @private
     */
    private createAdditionalStatusMessage(): void {
        if (this.totalAlerts.pending === this.getTotalRealServers()) {
            this.statusMessage = 'widget_ha_proxy_message';
        } else if (this.getTotalAlerts() === 0) {
            this.statusMessage = 'widget_everything_good';
        }
    }

    /**
     *
     * @private
     */
    private getTotalRealServers(): number {
        if (this.socketData && this.socketData.backends.length) {
            let totalReals = 0;
            for (const backend of this.socketData.backends) {
                totalReals = totalReals + backend.reals.array;
            }

            return totalReals;
        }
    }
}
